import React from 'react';
import { Divider, Text } from '@mantine/core';

import formatCurrency from '../../utility/formatCurrency';
import { When } from '../common';

const THIS_YEAR_COLOR = '#77E1DB';
const LAST_YEAR_COLOR = '#2A5EA7';

type MetricColumnProps = {
    label: string;
    quantity: string;
    value: string;
    swatchColor?: string;
    quantityClassName?: string;
    valueClassName?: string;
    showDivider?: boolean;
    showValue?: boolean;
};

function MetricColumn({
    label,
    quantity,
    value,
    swatchColor,
    quantityClassName = 'text-primary',
    valueClassName = 'text-[#4D5E80]',
    showDivider = true,
    showValue = true,
}: MetricColumnProps) {
    return (
        <>
            <div className="flex min-w-0 flex-1 flex-col gap-1 px-4 py-3">
                <div className="flex items-center gap-2">
                    <When condition={Boolean(swatchColor)}>
                        <span
                            className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
                            style={{ backgroundColor: swatchColor }}
                            aria-hidden
                        />
                    </When>
                    <Text className="text-xs font-bold uppercase text-[#7D8FB3]">
                        {label}
                    </Text>
                </div>
                <Text
                    className={`text-base font-bold tabular-nums leading-tight whitespace-nowrap ${quantityClassName}`}
                >
                    {quantity}
                </Text>
                <When condition={showValue}>
                    <Text
                        size="sm"
                        className={`tabular-nums font-medium whitespace-nowrap ${valueClassName}`}
                    >
                        {value}
                    </Text>
                </When>
            </div>
            <When condition={showDivider}>
                <Divider orientation="vertical" color="#e6e9ef" my="xs" />
            </When>
        </>
    );
}

type Props = {
    lastYearQuantity: number;
    lastYearValue: number;
    thisYearQuantity: number;
    thisYearValue: number;
    quantityDiff: number;
    valueDiff: number;
    currency: string;
    hideValue?: boolean;
};

function formatSignedQuantityLabel(diff: number): string {
    if (diff === 0) {
        return '0 units';
    }
    const absolute = Math.abs(diff).toLocaleString();
    return diff > 0 ? `+${absolute} units` : `−${absolute} units`;
}

function formatSignedCurrencyLabel(diff: number, currency: string): string {
    const formatted = formatCurrency(Math.abs(diff), currency);
    if (diff === 0) {
        return formatCurrency(0, currency);
    }
    return diff > 0 ? `+${formatted}` : `−${formatted}`;
}

export function ProductSalesSummaryMetrics({
    lastYearQuantity,
    lastYearValue,
    thisYearQuantity,
    thisYearValue,
    quantityDiff,
    valueDiff,
    currency,
    hideValue = false,
}: Props) {
    const isQuantityUp = quantityDiff > 0;
    const isQuantityDown = quantityDiff < 0;
    const isValueUp = valueDiff > 0;
    const isValueDown = valueDiff < 0;

    let diffQuantityClass = 'text-primary';
    if (isQuantityUp) {
        diffQuantityClass = 'text-emerald-600';
    } else if (isQuantityDown) {
        diffQuantityClass = 'text-red-600';
    }

    let diffValueClass = 'text-[#4D5E80]';
    if (isValueUp) {
        diffValueClass = 'text-emerald-700';
    } else if (isValueDown) {
        diffValueClass = 'text-red-700';
    }

    return (
        <div className="flex w-full items-stretch overflow-hidden rounded-lg border border-[#EDEFF2] bg-[#FAFBFC]">
            <MetricColumn
                label="Last Year"
                swatchColor={LAST_YEAR_COLOR}
                quantity={`${lastYearQuantity.toLocaleString()} units`}
                value={formatCurrency(lastYearValue, currency)}
                showValue={!hideValue}
            />
            <MetricColumn
                label="This Year"
                swatchColor={THIS_YEAR_COLOR}
                quantity={`${thisYearQuantity.toLocaleString()} units`}
                value={formatCurrency(thisYearValue, currency)}
                showValue={!hideValue}
            />
            <MetricColumn
                label="Difference"
                quantity={formatSignedQuantityLabel(quantityDiff)}
                value={formatSignedCurrencyLabel(valueDiff, currency)}
                quantityClassName={diffQuantityClass}
                valueClassName={diffValueClass}
                showDivider={false}
                showValue={!hideValue}
            />
        </div>
    );
}

export default ProductSalesSummaryMetrics;
